import React, { memo, useCallback, useMemo } from "react";
import { cn } from "utils";
import { deviceDetector } from "@ospweb/sdk-ui";
import Translations from "@translate/components/Chat/MemberStatusCard";
import { I18nUtil } from "@utils/i18n";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { Track } from "@/src/lib/tracking";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 用户状态: free/trial/complete
 * pro/expert 下架 memberStatus 已统一为 complete 状态
 */
export type ChatMemberStatus = "free" | "trial" | "complete";

export interface ChatMemberStatusFlags {
  isPro?: boolean;
  isExpert?: boolean;
  isTrial?: boolean;
}

/**
 * 获取用户当前会员状态，Pro/Expert 都下架，统一返回 complete （v2.8.0）
 * ed complete 的 level 是 Expert
 * @param memberInfo
 * @returns
 */
export const getChatMemberStatus = (
  memberInfo?: ChatMemberStatusFlags,
): ChatMemberStatus => {
  if (memberInfo?.isTrial) {
    return "trial";
  }
  if (memberInfo?.isExpert || memberInfo?.isPro) {
    return "complete";
  }
  return "free";
};

type ChatMemberButtonStyle = "filled" | "tonal" | "ghost";
export type ChatMemberCardVariant =
  "member-status" | "task-doable" | "task-claimable";

interface ChatMemberButtonConfig {
  text?: string;
  style?: ChatMemberButtonStyle;
  className?: string;
  visible?: boolean;
}

export interface ChatMemberStatusCardProps {
  memberStatus: ChatMemberStatus;
  variant?: ChatMemberCardVariant;
  title?: string;
  description?: string;
  resumeTimeText?: string;
  button?: {
    primary?: ChatMemberButtonConfig;
    secondary?: ChatMemberButtonConfig;
  };
  btnStyle?: {
    primary?: string;
    secondary?: string;
  };
  className?: string;
  trialPrice?: string | number;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  titleClassName?: string;
  descriptionClassName?: string;
  btnContainerClassName?: string;
  /** 是否正在进行应用内续费支付 */
  isAppPayingRenewLoading?: boolean;
  /** 是否在弹窗中显示 */
  showInModal?: boolean;
}

type ResolvedCardConfig = {
  title: string;
  description: string;
  primary: Required<
    Pick<ChatMemberButtonConfig, "text" | "style" | "visible">
  > &
    Pick<ChatMemberButtonConfig, "className">;
  secondary: Required<
    Pick<ChatMemberButtonConfig, "text" | "style" | "visible">
  > &
    Pick<ChatMemberButtonConfig, "className">;
};

const createDefaultConfig = (
  showInModal: boolean,
): Record<ChatMemberStatus, ResolvedCardConfig> => ({
  free: {
    title: I18n.t("Uh oh! You've reached your usage limit!"),
    description: I18n.t("Upgrade to continue reading and exploring with Ed"),
    primary: {
      text: showInModal
        ? I18n.t("Subscribe Ed Complete")
        : I18n.t("Upgrade to Ed Complete"),
      style: "filled",
      visible: true,
    },
    secondary: {
      text: I18n.t("See all plans"),
      style: "ghost",
      visible: true,
    },
  },
  trial: {
    title: I18n.t("Uh oh! You've reached your usage limit!"),
    description: I18n.t("Upgrade to continue reading and exploring with Ed"),
    primary: {
      text: I18n.t("Start Free Trial"),
      style: "filled",
      visible: true,
    },
    secondary: {
      text: I18n.t("See all plans"),
      style: "ghost",
      visible: true,
    },
  },
  /** @deprecated */
  // pro: {
  //   title: I18n.t("You've reached your Pro usage limit"),
  //   description: I18n.t(
  //     "You can resume your conversation soon. Check out other news while you wait.",
  //   ),
  //   primary: {
  //     text: I18n.t("Go"),
  //     style: "tonal",
  //     visible: true,
  //   },
  //   secondary: {
  //     text: "",
  //     style: "ghost",
  //     visible: false,
  //   },
  // },
  /** @deprecated */
  // expert: {
  //   title: I18n.t("You've reached your Expert usage limit"),
  //   description: I18n.t(
  //     "You can resume your conversation soon. Check out other news while you wait.",
  //   ),
  //   primary: {
  //     text: I18n.t("Go"),
  //     style: "tonal",
  //     visible: true,
  //   },
  //   secondary: {
  //     text: "",
  //     style: "ghost",
  //     visible: false,
  //   },
  // },
  complete: {
    title: I18n.t("Uh oh! You've reached your usage limit!"),
    description: I18n.t("Upgrade to continue reading and exploring with Ed"),
    primary: {
      text: I18n.t("Upgrade to Ed Complete"),
      style: "filled",
      visible: true,
    },
    secondary: {
      text: "See all plans",
      style: "ghost",
      visible: false,
    },
  },
});

const createTaskVariantConfig = (): Record<
  Exclude<ChatMemberCardVariant, "member-status">,
  Pick<ResolvedCardConfig, "title" | "description" | "primary" | "secondary">
> => ({
  "task-doable": {
    title: I18n.t("Keep the chat going!"),
    description: I18n.t(
      "You're out of credits! Earn more in just 2 minutes by completing a simple task.",
    ),
    primary: {
      text: I18n.t("Earn Credits"),
      style: "ghost",
      visible: true,
      className: "sm_button button_m min-w-[96px]",
    },
    secondary: {
      text: "",
      style: "tonal",
      visible: false,
    },
  },
  "task-claimable": {
    title: I18n.t("Free credits available"),
    description: I18n.t(
      "Claim your pending credits to unlock the chat and continue right where you left off.",
    ),
    primary: {
      text: I18n.t("Claim Credits"),
      style: "ghost",
      visible: true,
      className: "sm_button button_m min-w-[96px]",
    },
    secondary: {
      text: "",
      style: "tonal",
      visible: false,
    },
  },
});

const buttonStyleClassMap: Record<ChatMemberButtonStyle, string> = {
  filled: "button button_s xs_button primary_button",
  tonal: "button button_s xs_button secondary_button px-7",
  ghost: "button button_s xs_button",
};

const RESUME_DESCRIPTION_TEMPLATE = I18n.t(
  "You can resume your conversation on {resumeTime}. Check out Invest while you wait.",
);

const MemberStatusCard = ({
  memberStatus,
  variant = "member-status",
  title,
  description,
  button,
  btnStyle,
  className,
  titleClassName,
  descriptionClassName,
  btnContainerClassName,
  trialPrice,
  resumeTimeText,
  onPrimaryClick,
  onSecondaryClick,
  isAppPayingRenewLoading,
  showInModal = false,
}: ChatMemberStatusCardProps) => {
  const isInIOS = deviceDetector.isIOSApp();

  const resolved = useMemo<ResolvedCardConfig>(() => {
    const defaultConfig = createDefaultConfig(showInModal);
    const taskVariantConfig = createTaskVariantConfig();
    const base = defaultConfig[memberStatus];
    const variantDefaults =
      variant === "member-status" ? undefined : taskVariantConfig[variant];
    const primaryDefault = variantDefaults?.primary ?? base.primary;
    const secondaryDefault = variantDefaults?.secondary ?? base.secondary;

    // 试用状态下，根据 iOS 设备显示不同的按钮文案
    let primaryText = button?.primary?.text ?? primaryDefault.text;
    if (variant === "member-status" && memberStatus === "trial") {
      primaryText =
        button?.primary?.text ??
        (isInIOS
          ? I18n.t("Start free trial")
          : trialPrice
            ? I18n.t("Only $%{var0}", {
                var0: trialPrice,
              })
            : primaryDefault.text);
    }

    const resolvedDescription =
      variant === "member-status" && memberStatus === "complete"
        ? (description ??
          (resumeTimeText
            ? RESUME_DESCRIPTION_TEMPLATE.replace(
                "{resumeTime}",
                resumeTimeText,
              )
            : base.description))
        : (description ?? variantDefaults?.description ?? base.description);

    return {
      title: title ?? variantDefaults?.title ?? base.title,
      description: resolvedDescription,
      primary: {
        text: primaryText,
        style: button?.primary?.style ?? primaryDefault.style,
        className: button?.primary?.className ?? primaryDefault.className,
        visible: button?.primary?.visible ?? primaryDefault.visible,
      },
      secondary: {
        text: button?.secondary?.text ?? secondaryDefault.text,
        style: button?.secondary?.style ?? secondaryDefault.style,
        className: button?.secondary?.className ?? secondaryDefault.className,
        visible: button?.secondary?.visible ?? secondaryDefault.visible,
      },
    };
  }, [
    memberStatus,
    variant,
    title,
    description,
    button,
    isInIOS,
    trialPrice,
    resumeTimeText,
    showInModal,
  ]);

  /**
   * 本卡片点击的**唯一 OpenReplay 出口**（D2 方案 C · 2026-08-07）。
   *
   * 原来事件名是 `chat_credits_tips_${memberStatus}_${slot}_click_${variant}`，
   * 会展开成 **18 个**事件名（3 status × 3 variant × 主/次），而它们描述的是同一个按钮点击 ——
   * status/variant 是这次点击的**属性**，不是它的身份。再加一个 variant 就要多 6 个事件名、6 个看板。
   * 现在收敛成一个名，变量走参数。
   *
   * label 照常挂在按钮上（给 SDK 必然产生的 MouseClick 命名）；这里补的是参数维度，
   * 不算重复上报 —— 见规范 §5 规则 1。
   *
   * GA 侧的 `data-tracking-eventname` **保持旧的模板名不动**（存量冻结，规范 §0）。
   * 因此这一处 GA 与 OR 的事件名**不一致**，是收敛的必然代价：GA 看板继续按 18 个名走，
   * OR 侧按 1 个名 + 参数看。对照时用参数而不是名字。
   */
  const trackButtonClick = useCallback(
    (slot: "primary" | "secondary") => {
      // 业务事件，不用 `Track.click` —— 后者会把点击抄成无业务含义的 `user_click`。
      // 按钮文案不进名字也不进参数：回放里看得见，且随 i18n 变（2026-08-10 决策）。
      Track.event("chat_credits_tips_click", {
        slot,
        member_status: memberStatus,
        card_variant: variant,
      });
    },
    [memberStatus, variant],
  );

  const isTaskVariant = Object.keys(createTaskVariantConfig()).includes(
    variant,
  );
  const isMemberStatusVariant = variant === "member-status";

  return (
    <div
      className={cn(
        "relative rounded-[12px] border border-border_white_transparent10 bg-background_solid_primary",
        {
          "p-6": isTaskVariant,
          "p-4": isMemberStatusVariant,
        },
        className,
      )}
      data-tracking-eventname="chat_member_status_view"
    >
      <div
        className={cn(
          "body_bold_m text-content_primary",
          {
            headline_regular_s: isTaskVariant,
            body_bold_m: isMemberStatusVariant,
          },
          titleClassName,
        )}
      >
        {resolved.title}
      </div>
      <div
        className={cn(
          "body_m text-content_secondary",
          {
            "mt-2": isTaskVariant,
            "mt-1": isMemberStatusVariant,
          },
          descriptionClassName,
        )}
      >
        {resolved.description}
      </div>

      <div
        className={cn(
          "flex items-center gap-1",
          {
            "justify-end mt-6": !showInModal && isTaskVariant,
            "mt-4": isMemberStatusVariant,
          },
          btnContainerClassName,
        )}
      >
        {resolved.primary.visible && resolved.primary.text && (
          <button
            type="button"
            className={cn(
              buttonStyleClassMap[resolved.primary.style],
              btnStyle?.primary,
              resolved.primary.className,
              "group min-w-[76px]",
            )}
            data-loading={isAppPayingRenewLoading}
            data-disabled={isAppPayingRenewLoading}
            data-tracking-eventname={`chat_credits_tips_${memberStatus}_primary_click_${variant}`}
            data-openreplay-label="chatbox_memberstatuscard_click"
            onClick={() => {
              trackButtonClick("primary");
              onPrimaryClick?.();
            }}
          >
            <span className="group-loading:hidden">
              {resolved.primary.text}
            </span>
            <OspIcons
              name="ic_loading2"
              size={16}
              className={cn("hidden animate-spin group-loading:block")}
            />
          </button>
        )}

        {resolved.secondary.visible && resolved.secondary.text && (
          <button
            type="button"
            className={cn(
              buttonStyleClassMap[resolved.secondary.style],
              btnStyle?.secondary,
              resolved.secondary.className,
            )}
            data-tracking-eventname={`chat_credits_tips_${memberStatus}_secondary_click_${variant}`}
            data-openreplay-label="chatbox_memberstatuscard_click"
            onClick={() => {
              trackButtonClick("secondary");
              onSecondaryClick?.();
            }}
          >
            {resolved.secondary.text}
          </button>
        )}
      </div>
    </div>
  );
};

export default memo(MemberStatusCard);
